<template>
    <fe-dialog
        v-if="value"
        width="500"
        :title="`Merge With ${entityLabel}`"
        @dismiss="close"
        @accept="doMerge"
        :acceptButtonDisabled="!target || busy"
        acceptButtonText="Merge"
        persistent
        dismissButtonText="Cancel"
    >
        <div class="mb-3">
            The selected {{ entityLabelLower }}{{ selections.length === 1 ? '' : 's' }}
            ({{ selectionNames }}) will be merged into the {{ entityLabelLower }} you choose below.
            Their attendance records and aliases move to the chosen {{ entityLabelLower }}, and the
            merged {{ entityLabelLower }}{{ selections.length === 1 ? ' is' : 's are' }} kept as
            {{ selections.length === 1 ? 'an alias' : 'aliases' }}.
        </div>
        <v-form ref="mergeForm">
            <fe-remote-combo
                :items="targetItems"
                v-model="target"
                data-test="attendance-merge-target-combo"
                flat solo dense
                itemText="name"
                itemValue="id"
                required
                :placeholder="`Select a ${entityLabelLower}...`"
            />
        </v-form>
    </fe-dialog>
</template>

<script>
    /**
     * Reusable "Merge With ..." dialog for the attendance configuration grids
     * (statuses, reasons, excused codes). Picks a target record and posts each
     * selected source to the dedicated AttendanceMergeEndpoint.
     */
    export default {
        name: 'AttendanceMergeDialog',

        props: {
            // v-model: whether the dialog is shown
            value: {type: Boolean, default: false},
            // Backend property discriminator, e.g. 'AttendanceStatus'
            property: {type: String, required: true},
            // User-facing singular label, e.g. 'Status'
            entityLabel: {type: String, required: true},
            // Selected source rows (merged away)
            selections: {type: Array, default: () => []},
            // All rows currently in the grid (candidate targets)
            rows: {type: Array, default: () => []}
        },

        data() {
            return {
                target: null,
                busy: false
            }
        },

        computed: {
            entityLabelLower() {
                return this.entityLabel.toLowerCase()
            },

            selectionNames() {
                return this.selections.map(r => r.name).join(', ')
            },

            // Candidate targets are all rows except the ones being merged away.
            targetItems() {
                let ids = this.selections.map(r => r.id)
                return this.rows.filter(r => ids.indexOf(r.id) === -1)
            }
        },

        watch: {
            value(shown) {
                if (shown) {
                    this.target = null
                }
            }
        },

        methods: {
            close() {
                this.$emit('input', false)
            },

            doMerge() {
                if (!this.target || !this.selections.length) {
                    return
                }

                let targetId = this.target.id
                let url = this.$models.getUrl('attendanceMerge', 'merge', {property: this.property})

                this.busy = true
                // One transactional request per source; the endpoint merges a
                // single source into the target at a time.
                Promise.all(
                    this.selections.map(src => this.$axios.post(url, {
                        id: src.id,
                        merge_to_id: targetId
                    }))
                ).then(responses => {
                    // The endpoint returns HTTP 200 with {success:false, msg} on a
                    // handled failure, so inspect the payload rather than trusting
                    // the HTTP status alone.
                    let failed = responses.filter(r => r && r.data && r.data.success === false)
                    if (failed.length) {
                        this.emitMergeError((failed[0].data && failed[0].data.msg) || null)
                    } else {
                        let last = responses[responses.length - 1]
                        let msg = (last && last.data && last.data.msg) || 'Merge successful.'
                        this.$snackbars.$emit('new', {text: msg, usage: 'success'})
                    }
                    // Each request is its own transaction, so some may have
                    // succeeded even on failure. Refresh and close either way: the
                    // parent's reload deselects the grid, so keeping the dialog open
                    // would just leave it with an empty selection. The user can
                    // re-select and retry from the refreshed grid.
                    this.finishMerge()
                }).catch(error => {
                    // Network error or non-2xx response.
                    this.emitMergeError((error && error.response && error.response.data && error.response.data.msg) || null)
                    this.finishMerge()
                }).finally(() => {
                    this.busy = false
                })
            },

            emitMergeError(msg) {
                this.$snackbars.$emit('new', {
                    text: msg || 'Unable to merge. Please try again.',
                    usage: 'error'
                })
            },

            // Refresh the parent grid (reflecting full or partial success) and
            // close the dialog. Runs on both success and failure.
            finishMerge() {
                this.$emit('merged')
                this.close()
            }
        }
    }
</script>

<style scoped></style>
